import React from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import { convertRoutePathToLink, dateStringToYYYYMMDD } from '@/utils/functions';
import { routesPath } from '@/utils/routesPath';
import { Button, Table, Tag } from 'antd';
import { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { studentsApi } from '../../../api/students/studentsApi';
import { STUDENT_STATUS_COLOR, STUDENT_STATUS_TEXT } from '@/utils/constants';
import { useTranslation } from 'react-i18next'; // Added import

const MySwal = withReactContent(Swal);
const URL = convertRoutePathToLink(routesPath.STUDENTS);

const ListComponent = ({ ...props }) => {
    const { t } = useTranslation(); // Added hook
    const [students, setStudents] = useState([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0
    });

    const [searchName, setSearchName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [searchStatus, setSearchStatus] = useState('');

    const getListStudent = (page = pagination.page) => {
        setIsLoadingStudents(true);

        const filterParams = {};
        if (searchName) {
            filterParams.firstName = searchName;
            filterParams.lastName = searchName;
        }
        if (searchEmail) filterParams.email = searchEmail;
        if (searchStatus) filterParams.status = searchStatus;

        const apiParams = {
            page: page,
            pageSize: pagination.pageSize
        };

        if (Object.keys(filterParams).length > 0) {
            apiParams.filter = JSON.stringify(filterParams);
        }

        studentsApi
            .getList(apiParams)
            .then((res) => {
                setIsLoadingStudents(false);
                setStudents(res.data.data.rows);
                setPagination({
                    ...pagination,
                    page: page,
                    total: res.data.data.total
                });
            })
            .catch((err) => {
                console.error(err);
                setIsLoadingStudents(false);
            });
    };

    useEffect(() => {
        getListStudent();
    }, [pagination.page]);

    const handleDelete = (id) => {
        MySwal.fire({
            title: t('Student_Admin_List_Delete_Title'),
            text: t('Student_Admin_List_Delete_Text'),
            icon: 'warning',
            showCancelButton: true,
            confirmColor: '#3085d6',
            cancelColor: '#d33',
            confirmText: t('Student_Admin_List_Delete_ConfirmButton')
        }).then((result) => {
            if (result.isConfirmed) {
                studentsApi
                    .delete(id)
                    .then((res) => {
                        MySwal.fire(
                            t('Student_Admin_List_Delete_SuccessTitle'),
                            t('Student_Admin_List_Delete_SuccessText'),
                            'success'
                        );
                        getListStudent();
                    })
                    .catch((err) => console.error(err));
            }
        });
    };

    const handleSearch = () => {
        getListStudent(1);
    };

    const columns = [
        {
            title: t('Student_Admin_List_Table_Name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <>
                    {record?.firstName} {record?.lastName}
                </>
            )
        },
        {
            title: t('Student_Admin_List_Table_Email'),
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: t('Student_Admin_List_Table_Phone'),
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: t('Student_Admin_List_Table_DOB'),
            dataIndex: 'dateOfBirth',
            key: 'dateOfBirth',
            render: (text, record) => (
                <>{record?.dateOfBirth ? dateStringToYYYYMMDD(record?.dateOfBirth) : '-'}</>
            )
        },
        {
            title: t('Student_Admin_List_Table_Gender'),
            dataIndex: 'gender',
            key: 'gender'
        },
        {
            title: t('Student_Admin_List_Table_Status'),
            dataIndex: 'status',
            key: 'status',
            render: (text, record) => (
                <Tag color={STUDENT_STATUS_COLOR[record?.status]}>
                    {STUDENT_STATUS_TEXT[record?.status]}
                </Tag>
            )
        },
        {
            title: '',
            dataIndex: 'action',
            key: 'action',
            render: (text, record) => {
                return (
                    <>
                        <Link to={URL + '/edit?id=' + record?.id}>
                            <button
                                type="button"
                                className="btn btn-icon btn-sm"
                                title={t('Student_Admin_List_Button_Edit')}>
                                <i className="fa fa-edit"></i>
                            </button>
                        </Link>
                        <button
                            onClick={() => handleDelete(record.id)}
                            type="button"
                            className="btn btn-icon btn-sm js-sweetalert"
                            title={t('Student_Admin_List_Button_Delete')}
                            data-type="confirm">
                            <i className="fa fa-trash-o text-danger"></i>
                        </button>
                    </>
                );
            }
        }
    ];

    return (
        <>
            <div className="section-body">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="header-action">
                            <h1 className="page-title">{t('Student_Admin_List_PageTitle')}</h1>
                            <ol className="breadcrumb page-breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="/">{t('Student_Admin_List_Breadcrumb_Brand')}</a>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page">
                                    {t('Student_Admin_List_Breadcrumb_Active')}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="section-body mt-4">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-4 col-md-4 col-sm-6">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t(
                                                'Student_Admin_List_Filter_Name_Placeholder'
                                            )}
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-4 col-sm-6">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t(
                                                'Student_Admin_List_Filter_Email_Placeholder'
                                            )}
                                            value={searchEmail}
                                            onChange={(e) => setSearchEmail(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <div className="input-group">
                                        <select
                                            className="form-control show-tick"
                                            value={searchStatus}
                                            onChange={(e) => setSearchStatus(e.target.value)}>
                                            <option
                                                value=""
                                                disabled
                                                selected>
                                                {t('Student_Admin_List_Filter_Status_Placeholder')}
                                            </option>
                                            <option value="NOT_TESTED">
                                                {t('Student_Admin_List_Filter_Status_NotTested')}
                                            </option>
                                            <option value="TESTED">
                                                {t('Student_Admin_List_Filter_Status_Tested')}
                                            </option>
                                            <option value="STUDYING">
                                                {t('Student_Admin_List_Filter_Status_Studying')}
                                            </option>
                                            <option value="COMPLETED">
                                                {t('Student_Admin_List_Filter_Status_Completed')}
                                            </option>
                                            <option value="ON_LEAVE">
                                                {t('Student_Admin_List_Filter_Status_Inactive')}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-2 col-md-4 col-sm-6">
                                    <button
                                        className="btn btn-sm btn-primary btn-block"
                                        onClick={handleSearch}>
                                        {t('Student_Admin_List_Filter_Button_Search')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex w-full justify-end my-4">
                        <Link to={URL + '/edit'}>
                            <Button
                                className=" !bg-[#0d356b] !text-white hover:!bg-[#A40000] hover:!text-white hover:!border-[#A40000]"
                                size="large">
                                {t('Student_Admin_List_Button_Create')}
                            </Button>
                        </Link>
                    </div>
                    <div className="table-responsive card">
                        <Table
                            loading={isLoadingStudents}
                            columns={columns}
                            dataSource={students}
                            pagination={{
                                pageSize: pagination.pageSize,
                                current: pagination.page,
                                total: pagination.total,
                                onChange: (e) => setPagination({ ...pagination, page: e })
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

ListComponent.displayName = 'List';

const List = (props) => {
    return (
        <ErrorWrapper>
            <ListComponent
                data-id="List-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default List;
